import React from "react";
import { Link } from "react-router-dom";
import { Lightning, ArrowRight } from "@phosphor-icons/react";

/**
 * Banner de Campaña Sociedades — aparece en Landing y La Máquina hasta julio 2026.
 * Visible solo si la fecha actual está antes de la fecha de cierre.
 */
export default function CampanaSociedadesBanner({ variant = "full" }) {
    // Fecha de cierre de la campaña (25 julio 2026 — última fecha para presentar Impuesto Sociedades)
    const CAMPAIGN_END = new Date("2026-07-25T23:59:59");
    if (new Date() > CAMPAIGN_END) return null;

    // Calcular semanas que faltan para la presentación del Impuesto de Sociedades (25 julio)
    const weeksLeft = Math.max(1, Math.ceil((CAMPAIGN_END - new Date()) / (1000 * 60 * 60 * 24 * 7)));

    if (variant === "compact") {
        return (
            <div className="bg-[var(--gf-warning)] text-[var(--gf-navy)] py-2 px-4 text-center text-sm font-semibold print:hidden" data-testid="campana-banner-compact">
                <Lightning size={14} weight="fill" className="inline mr-1" />
                Campaña Sociedades 2025 · {weeksLeft} semanas para firmar el balance que tu banco verá durante 12 meses ·{" "}
                <Link to="/maquina-verdad" className="underline underline-offset-2 hover:text-[var(--gf-gold)]" data-testid="campana-banner-cta">
                    Revisar gratis →
                </Link>
            </div>
        );
    }

    return (
        <section
            className="bg-gradient-to-r from-[var(--gf-navy)] to-[#0f2540] text-white py-16 md:py-24 relative overflow-hidden print:hidden border-y-2 border-[var(--gf-gold)]/30"
            data-testid="campana-banner-full"
        >
            {/* Acentos decorativos */}
            <div className="absolute inset-0 opacity-10 pointer-events-none">
                <div className="absolute top-1/2 left-1/4 w-[32rem] h-[32rem] bg-[var(--gf-gold)] rounded-full blur-3xl"></div>
                <div className="absolute bottom-0 right-0 w-96 h-96 bg-[var(--gf-warning)] rounded-full blur-3xl"></div>
            </div>

            <div className="relative max-w-4xl mx-auto px-6 md:px-10">
                {/* Etiquetas superiores */}
                <div className="flex flex-wrap items-center gap-3 mb-6">
                    <div className="px-4 py-2 bg-[var(--gf-warning)] text-[var(--gf-navy)] font-bold text-xs md:text-sm tracking-widest uppercase animate-pulse shadow-lg">
                        <Lightning size={13} weight="fill" className="inline mr-1.5" /> Campaña Sociedades 2025
                    </div>
                    <div className="px-4 py-2 border border-[var(--gf-gold)]/50 text-[var(--gf-gold)] text-xs md:text-sm tracking-widest uppercase font-semibold">
                        {weeksLeft} semanas restantes
                    </div>
                </div>

                {/* Titular destacado */}
                <h2 className="serif text-4xl md:text-6xl leading-[1.05] mb-8 font-medium">
                    Estás a <span className="text-[var(--gf-gold)] italic">{weeksLeft} semanas</span> de firmar
                    el balance que el banco usará contra ti durante <strong>12 meses</strong>.
                </h2>

                {/* Bloque de texto justificado — forma un cuadrado perfecto */}
                <div
                    className="text-lg md:text-xl text-white/85 leading-[1.7] mb-10 space-y-5"
                    style={{ textAlign: "justify", textJustify: "inter-word", hyphens: "auto" }}
                >
                    <p>
                        El balance que liquides ahora aparecerá en el Impuesto sobre Sociedades — y será el
                        documento que tu banco verá hasta <strong className="text-white">julio de 2027</strong>.
                        Doce meses enteros decidiendo si te conceden financiación o no, basándose en cifras
                        que firmaste sin una segunda opinión profesional.
                    </p>
                    <p>
                        ¿Realmente confías en que tu asesoría lo está optimizando pensando en tu banco?
                        <strong className="text-[var(--gf-gold)]"> ¿19€ no merecen la pena</strong> por una
                        revisión profesional antes de firmar el documento más importante del año fiscal?
                    </p>
                </div>

                {/* CTA destacado */}
                <div className="flex flex-wrap gap-4 items-center">
                    <Link to="/maquina-verdad" className="btn-gold text-base md:text-lg px-8 py-4" data-testid="campana-banner-cta-full">
                        Probar gratis ahora <ArrowRight size={18} />
                    </Link>
                    <span className="text-sm text-white/65 italic">60 segundos · Sin registro · Sin tarjeta</span>
                </div>
            </div>
        </section>
    );
}
